import { useState, useEffect, useMemo } from "react";
import { Card, Calendar, Button, Checkbox } from "../../../components";
import { RefreshCw, CalendarX, XCircle } from "lucide-react";
import axios from "axios";
import {
  TRAININGS_PERIOD_URL,
  SUBSCRIBE_URL,
  ACTIVE_SUBSCRIBER_EMAILS_URL,
  SEND_BULK_EMAIL_URL,
} from "../../../features/UrlHelper";
import { handleApiError } from "../../../features/errorHandler";

const authHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
});

function getSubStatus(sub) {
  if (sub.is_refunded) return "refunded";
  const now = new Date();
  const from = new Date(sub.valid_from);
  const to = new Date(sub.valid_to);
  if (now < from) return "pending";
  if (now > to) return "expired";
  return "active";
}

function mapEvents(data) {
  return data
    .filter((e) => e.title)
    .map((e) => ({
      date: e.date,
      title: e.title,
      description: e.description ?? "",
      timeFrom: e.time_from,
      timeTo: e.time_to,
      color: e.color,
    }));
}

function getMonthRange(date) {
  const from = new Date(date.getFullYear(), date.getMonth(), 1);
  const to = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { from, to };
}

function formatSKDate(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}.${m}.${y}`;
}

// ─── Bulk Email Panel ──────────────────────────────────────────────────────────
function BulkEmailPanel() {
  const [emailRows, setEmailRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success'|'error', text }

  useEffect(() => {
    loadEmails();
  }, []);

  function loadEmails() {
    setLoadingEmails(true);
    setStatus(null);
    axios
      .get(ACTIVE_SUBSCRIBER_EMAILS_URL, { headers: { ...authHeader() } })
      .then((res) => {
        setEmailRows(res.data);
        setSelected(new Set(res.data.map((r) => r.email)));
      })
      .catch((err) =>
        setStatus({ type: "error", text: handleApiError(err, "Nepodarilo sa načítať emaily.") })
      )
      .finally(() => setLoadingEmails(false));
  }

  function toggleEmail(email) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  }

  const allSelected = emailRows.length > 0 && selected.size === emailRows.length;

  function toggleAll() {
    setSelected(
      allSelected ? new Set() : new Set(emailRows.map((r) => r.email))
    );
  }

  function fillTomorrow() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const d = formatSKDate(tomorrow);
    setSubject(`Zrušenie tréningu – ${d}`);
    setMessage(
      `Ahoj,\n\ntréning dňa ${d} od [doplňte] sa ruší.\nNáhrada bude dňa [doplňte] o [doplňte].\n\nĎakujeme za pochopenie.\n\nCapoeira tím`
    );
  }

  function fillToday() {
    const today = new Date();
    const d = formatSKDate(today);
    setSubject(`Zrušenie dnešného tréningu – ${d}`);
    setMessage(
      `Ahoj,\n\ndnešný tréning dňa ${d} sa ruší.\n\nOspravedlňujeme sa za nepríjemnosti.\n\nCapoeira tím`
    );
  }

  async function handleSend() {
    const emails = [...selected];
    if (!emails.length || !subject.trim() || !message.trim()) return;
    setSending(true);
    setStatus(null);
    try {
      const res = await axios.post(
        SEND_BULK_EMAIL_URL,
        { emails, subject, message },
        { headers: { ...authHeader() } }
      );
      setStatus({ type: "success", text: res.data.message });
      setSubject("");
      setMessage("");
    } catch (err) {
      setStatus({
        type: "error",
        text: handleApiError(err, "Chyba pri odosielaní."),
      });
    } finally {
      setSending(false);
    }
  }

  const canSend =
    selected.size > 0 && subject.trim().length > 0 && message.trim().length > 0;

  return (
    <Card className="bulk-email">
      {/* Header row */}
      <Card.Header className="bulk-email__header">
        <div className="bulk-email__title-group">
          <Card.Title as="h3" className="bulk-email__title">Hromadný e-mail</Card.Title>
          <span className="bulk-email__badge">
            {loadingEmails ? "…" : `${selected.size} / ${emailRows.length}`}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={loadEmails}
          disabled={loadingEmails}
          title="Obnoviť zoznam"
        >
          <RefreshCw size={15} />
        </Button>
      </Card.Header>

      <Card.Body className="bulk-email__body">
        {/* Quick-fill buttons */}
        <div className="bulk-email__quick-row">
          <button className="bulk-email__quick-btn" onClick={fillTomorrow}>
            <span className="bulk-email__quick-icon"><CalendarX size={22} /></span>
            <span>
              <strong>Zrušenie zajtrajšieho tréningu</strong>
              <small>Predvyplní šablónu s dátumom zajtra</small>
            </span>
          </button>
          <button className="bulk-email__quick-btn" onClick={fillToday}>
            <span className="bulk-email__quick-icon"><XCircle size={22} /></span>
            <span>
              <strong>Zrušenie dnešného tréningu</strong>
              <small>Predvyplní šablónu s dnešným dátumom</small>
            </span>
          </button>
        </div>

        {/* Email compose */}
        <div className="bulk-email__compose">
          <div className="bulk-email__field">
            <label className="bulk-email__label">Predmet</label>
            <input
              className="bulk-email__input"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Predmet e-mailu…"
            />
          </div>
          <div className="bulk-email__field">
            <label className="bulk-email__label">Správa</label>
            <textarea
              className="bulk-email__textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Text správy…"
              rows={6}
            />
          </div>
        </div>

        {/* Recipient list */}
        <div className="bulk-email__recipients">
          <div className="bulk-email__recipients-header">
            <div className="bulk-email__check-row--all">
              <Checkbox
                checked={allSelected}
                onChange={toggleAll}
                label={allSelected ? "Odznačiť všetkých" : "Vybrať všetkých"}
              />
            </div>
          </div>

          {loadingEmails ? (
            <div className="bulk-email__empty">Načítavam emaily…</div>
          ) : emailRows.length === 0 ? (
            <div className="bulk-email__empty">Žiadni aktívni odberatelia.</div>
          ) : (
            <div className="bulk-email__list">
              {emailRows.map((row) => (
                <div
                  key={row.email}
                  className={`bulk-email__check-row${selected.has(row.email) ? " bulk-email__check-row--checked" : ""}`}
                >
                  <Checkbox
                    checked={selected.has(row.email)}
                    onChange={() => toggleEmail(row.email)}
                  />
                  <span className="bulk-email__email-name">{row.userName}</span>
                  <span className="bulk-email__email-addr">{row.email}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Status */}
        {status && (
          <div className={`bulk-email__status bulk-email__status--${status.type}`}>
            {status.text}
          </div>
        )}

        {/* Send */}
        <Button
          variant="primary"
          onClick={handleSend}
          disabled={!canSend || sending}
        >
          {sending ? "Odosielam…" : `Odoslať na ${selected.size} adresát${selected.size === 1 ? "a" : "ov"}`}
        </Button>
      </Card.Body>
    </Card>
  );
}

// ─── Main Section ──────────────────────────────────────────────────────────────
function OverviewSection() {
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);

  useEffect(() => {
    const { from, to } = getMonthRange(new Date());
    loadCalendarEvents(from, to);
    loadSubscriptions();
  }, []);

  function loadSubscriptions() {
    axios
      .get(SUBSCRIBE_URL, {
        headers: { ...authHeader() },
      })
      .then((res) => setSubscriptions(res.data))
      .catch((err) => handleApiError(err, "Nepodarilo sa načítať predplatné."));
  }

  const stats = useMemo(() => {
    const now = new Date();
    const in30Days = new Date();
    in30Days.setDate(in30Days.getDate() + 30);

    const active = subscriptions.filter((s) => getSubStatus(s) === "active");
    const expiringSoon = active.filter((s) => new Date(s.valid_to) <= in30Days);
    const refunded = subscriptions.filter(
      (s) => getSubStatus(s) === "refunded",
    );

    const thisMonth = subscriptions.filter((s) => {
      const created = new Date(s.valid_from);
      return (
        created.getMonth() === now.getMonth() &&
        created.getFullYear() === now.getFullYear() &&
        !s.is_refunded
      );
    });

    const revenue = active.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);

    return {
      total: subscriptions.length,
      active: active.length,
      expiringSoon: expiringSoon.length,
      refunded: refunded.length,
      newThisMonth: thisMonth.length,
      revenue,
    };
  }, [subscriptions]);

  async function loadCalendarEvents(from, to) {
    try {
      const response = await axios.post(TRAININGS_PERIOD_URL, {
        date_from: from.toISOString().split("T")[0],
        date_to: to.toISOString().split("T")[0],
      });
      setCalendarEvents(mapEvents(response.data));
    } catch (error) {
      handleApiError(error, "Nepodarilo sa načítať kalendár tréningov.");
    }
  }

  return (
    <div className="admin__section">
      <h2 className="admin__section-title">Prehľad</h2>

      <div className="admin__stats">
        <Card className="admin__stat-card">
          <Card.Body>
            <span className="admin__stat-value">{stats.active}</span>
            <span className="admin__stat-label">Aktívnych predplatných</span>
          </Card.Body>
        </Card>
        <Card className="admin__stat-card">
          <Card.Body>
            <span className="admin__stat-value">{stats.newThisMonth}</span>
            <span className="admin__stat-label">Nových tento mesiac</span>
          </Card.Body>
        </Card>
        <Card className="admin__stat-card">
          <Card.Body>
            <span className="admin__stat-value">{stats.expiringSoon}</span>
            <span className="admin__stat-label">Končí do 30 dní</span>
          </Card.Body>
        </Card>
        <Card className="admin__stat-card">
          <Card.Body>
            <span className="admin__stat-value">
              {stats.revenue.toFixed(2)} €
            </span>
            <span className="admin__stat-label">Príjem (aktívne)</span>
          </Card.Body>
        </Card>
        <Card className="admin__stat-card">
          <Card.Body>
            <span className="admin__stat-value">{stats.refunded}</span>
            <span className="admin__stat-label">Refundovaných</span>
          </Card.Body>
        </Card>
        <Card className="admin__stat-card">
          <Card.Body>
            <span className="admin__stat-value">{stats.total}</span>
            <span className="admin__stat-label">Celkom predplatných</span>
          </Card.Body>
        </Card>
      </div>

      <BulkEmailPanel />
    </div>
  );
}

export default OverviewSection;
